import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { codeSnippets } from "../../pages/expressions/codesnippets";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../../lib/utils";

const defaultDataset = { Id:0, Title: "None", Description: "", OrderId: -1, CategoryId: 0, Columns:[], Expressions: [], FieldGroups: [] };
const defaultDatasetId = 0;

const defaultDataType = {  Id:0, Title: "None", Description: "", OrderId: -1 }
const defaultDataTypeId = 0;

const defaultFieldGroup = { Id: 0, Title: "All Fields", OrderId: -1};
const defaultFieldGroupId = 0;

const defaultFormInitialValues = {
    Id: 0, 
    DatasetId: 0,
    Title: "",
    Name: "",
    IsVisibleToClient: false,
    Description: "", 
    DataTypeId: defaultDataTypeId, 
    FieldGroupId: defaultFieldGroupId,
    Body: "", 
    Checks:[false, false, false],
    IsAggregate: false, IsDecimal: false, IsPercent: false,
    IsSystem: false,
    OrderId: 0
};

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    mode: 0, //0 no mode, 1 new mode, 2 edit mode  
    currentDataset: defaultDataset,
    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [], 
    currentFieldGroupId: defaultFieldGroupId,
    lookups: {
        datasets: [],
        dataTypes: []
    },
    toolsVisible: true,
    panes: [ 
        {
            size:"20%",
            min: "18%",
            max: "25%",
            collapsible: false
        },           
        {
            min: "53%",
            max: "66%",
            collapsible: false,
        },
        {
            size:"18%",
            min: "16%",
            max: "22%",
            collapsible: false,
        }    
      ],
};

const expressionsPageSlice = createSlice({
    name: "expressionsPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),
        
        setFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                formValues: {...state.formValues, [id]: value}
            });
        },

        setFormClientErrors: (state, action) => ({...state, formClientErrors: {...action.payload}}),

        setFormServerErrors: (state, action) => ({...state, formServerErrors: [...action.payload]}),

        pageLoaded : (state, action) => {
            //payload: {Datasets: includes the default one , DataTypes: includes the default }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                mode: 0, 
                currentDataset: defaultDataset,
                formKey: (new Date()).toString(), formValues:{...defaultFormInitialValues}, formInitialValues:{...defaultFormInitialValues}, formClientErrors:{}, formServerErrors:[],            
                currentFieldGroupId: defaultFieldGroupId,            
                lookups:{ datasets: action.payload.Datasets, dataTypes: action.payload.DataTypes},
            };
        },

        datasetLoaded: (state, action) => {
            //payload: {dataset object}
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;
                
            let loadedEntity = action.payload;

            //update the loaded dataset info inside lookups            
            let newLookups = {...pageState.lookups, datasets: [...pageState.lookups.datasets.filter(e => e.Id != loadedEntity.Id), {Id: loadedEntity.Id, Title: loadedEntity.Title, Description: loadedEntity.Description, OrderId: loadedEntity.OrderId, CategoryId: loadedEntity.CategoryId }]};

            return ({
                ...state,
                processing: false, processingText: "", 
                mode: 0, 
                currentDataset: {...action.payload, FieldGroups:[defaultFieldGroup, ...action.payload.FieldGroups]}, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: { }, formServerErrors: [], 
                currentFieldGroupId: defaultFieldGroupId,
                lookups: newLookups
            });
        },

        currentFieldGroupSelected: (state, action) => {
            //payload: the id of the field group                     
            return ({...state, currentFieldGroupId: action.payload});
        },
      
        entityLoaded: (state, action) => {
            //action.payload: the fetched entity
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            return {...state, 
                processing: false, processingText: "",
                mode: 2, 
                formKey: (new Date()).toString(), formInitialValues: {...action.payload}, formValues: {...action.payload}, 
                formClientErrors: {}, formServerErrors: [],             
            };
        },         

        createEntity: (state, action) => {
            const pageState = current(state);
            const currentDatasetId = pageState.currentDataset.Id;

            return ({
                ...state, 
                mode: 1, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues, DatasetId: currentDatasetId}, formValues: {...defaultFormInitialValues, DatasetId: currentDatasetId}, formClientErrors: {}, formServerErrors: []
            });
        },

        entityInserted: (state, action) => {
            //payload: {id: , orderId: }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const id = action.payload.id;
            const orderId = action.payload.orderId;

            const insertedEntity = {...pageState.formValues, Id: id, OrderId: orderId};
             
            let currentDataset = {...pageState.currentDataset};
          
            //add entity to it
            currentDataset = {...currentDataset, Expressions: [...currentDataset.Expressions, insertedEntity]};

            return ({
                ...state, 
                mode: 2, 
                currentDataset: currentDataset, 
                formKey: (new Date()).toString(), formValues: {...insertedEntity}, formInitialValues: {...insertedEntity}, formClientErrors: {}, formServerErrors: []
            });
        },

        entityUpdated: (state, action) => {
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const entityId = pageState.formValues.Id;

            //rebind in list
            let currentDataset = {...pageState.currentDataset};

            //remove -> add entity to it
            currentDataset = {...currentDataset, Expressions: currentDataset.Expressions.map(item => {
                if (item.Id == entityId)
                   return {...pageState.formValues};
                else
                    return item;
            })};

            return ({
                ...state, 
                currentDataset: currentDataset, 
                formKey: (new Date()).toString(), formInitialValues: {...state.formValues}, formClientErrors:{}, formServerErrors: []
            });
        },
        
        entityCloned: (state, action) => {
            //payload: {id: , title: , navigate:  }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const id = action.payload.id;
            const title = action.payload.title;
            const navigate = action.payload.navigate;

            const insertedEntity = {...pageState.formValues, Id: id, Title: title, OrderId: 0, IsSystem: false};
             
            let currentDataset = {...pageState.currentDataset};
          
            //add entity to it
            currentDataset = {...currentDataset, Expressions: [...currentDataset.Expressions, insertedEntity]};

            return ({
                ...state, 
                currentDataset: currentDataset
            });
        },

        removeEntityFromUI: (state, action) => {
            //payload: the entity id
            let pageState = current(state);
            let entityId = action.payload;
            let newCurrentDataset = {...pageState.currentDataset};

            //compute currentDataset, IF it contains the removed entity, by removing the expression from it            
            if (newCurrentDataset.Expressions.find(e => e.Id == entityId))
                newCurrentDataset = {...newCurrentDataset, Expressions: newCurrentDataset.Expressions.filter(e => e.Id != entityId)};
          
            //if it's the current entity => goto no mode
            let currentMode = {};
            if (pageState.mode == 2 && pageState.formValues.Id == entityId)
               currentMode = {
                mode: 0, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formClientErrors: {}, formServerErrors: [] 
            };

            return ({...state, currentDataset: newCurrentDataset, ...currentMode});
        },

        entityDeleted: (state, action) => {
            //payload: id of the deleted entity
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            const deletedEntityId = action.payload;
            let isCurrentEntity = false;

            if (pageState.formValues.Id == deletedEntityId)
                isCurrentEntity = true;
            
            //remove the expression from currentDataset
            let newCurrentDataset = {...pageState.currentDataset, Expressions: [...pageState.currentDataset.Expressions.filter(e => e.Id != deletedEntityId)]};
            
            let newMode={};
            if(isCurrentEntity)
                newMode={
                    mode: 0, 
                    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, 
                    formClientErrors: {}, formServerErrors: []
                };

            return ({...state, currentDataset:newCurrentDataset, ...newMode});
        },

        insertCodeSnippet: (state, action) => {
            //payload: {formRenderProps: , id: }
            let currentBody = current(state).formValues.Body || "";
            let formRenderProps = action.payload.formRenderProps;
            let id = action.payload.id;

            let body = codeSnippets.find(e => e.id == id).body;

            formRenderProps.onChange("Body", {
                value: currentBody +  body
            });

            return ({...state, formValues:{...state.formValues, Body: currentBody + body}});
        },        

        insertFunction: (state, action) => {
            let currentBody = current(state).formValues.Body || "";
            let formRenderProps = action.payload.formRenderProps;
            let func = action.payload.function;

            formRenderProps.onChange("Body", {
                value: currentBody + func
            });

            return ({...state, formValues:{...state.formValues, Body: currentBody + func}});
        },

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default expressionsPageSlice.reducer;


export const loadPage = (t) => async (dispatch, getState) => {
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'expression/getexpressionspagedata',
                method:  'post',
                data: {
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
            return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        //add the 0/None dataset
        data.Datasets.splice(0, 0, {...defaultDataset});

        //add the 0/None to data types
        data.DataTypes.splice(0, 0, {...defaultDataType});
       
        dispatch(pageLoaded(data));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
    //show loading
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingDataset")}));

    if (datasetId == 0)
    {   
        dispatch(datasetLoaded({...defaultDataset}));
        return;
    }

    try{
        var response = await api({            
            url: "dataset/getdatasetbyid",
            method:  'post',
            data: {
                id: datasetId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(datasetLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {
    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingExpression")}));

    try{
        var response = await api({            
            url: "expression/getexpressionbyid",
            method:  'post',
            data: {
                id: entityId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
           return;

        let data = response.data.Data;

        //transform data
        //checks
        data.Checks = [data.IsAggregate, data.IsDecimal, data.IsPercent];

        dispatch(entityLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const saveEntity = (t) => async (dispatch, getState) => {
    const pageState = getState().expressionsPage;

    let entityId = pageState.formValues.Id;
    let url = pageState.mode == 1 ? "expression/createexpression" : "expression/updateexpression";
    let title = pageState.mode == 1 ? t("slices.creatingExpression") : t("slices.updatingExpression");

    //show loader
    dispatch(setProcessing({processing: true, processingText: title}));

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {...pageState.formValues},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
           return;

        let data = response.data.Data;

        if (pageState.mode == 1)
            dispatch(entityInserted({id: data.Id, orderId: data.OrderId}));
        else
            dispatch(entityUpdated());
        
        let successMessage = pageState.mode == 1 ? t("slices.createdExpression") : t("slices.updatedExpression");

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingExpression")}));

    try{
        var response = await api({            
            url:"/expression/removeexpression",
            method:  'post',
            data: {id: id},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.deletedExpression"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(entityDeleted(id));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const testExpression = (t) => async (dispatch, getState) => 
{
    const pageState = getState().expressionsPage;
    let datasetId = pageState.currentDataset.Id;
    let body = pageState.formValues["Body"];

    dispatch(setProcessing({processing: true, processingText: t("slices.testingExpression")}));

    try{
        var response = await api({            
            url:"/expression/testexpression",
            method:  'post',
            data: {datasetId: datasetId, body: body},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().expressionsPage.pageDisposed)
           return;

        let data = response.data.Data;

        let message ="";

        if (data.IsValid)
        {
            let dataType = "";

            if (data.DataType != null)
                dataType = (data.DataType == 2) ? " numeric " : (data.DataType == 1 ? "boolean " : "date/time ");
            
            if (dataType)
                message = t("slices.validExpression") + dataType +  (data.IsAggregate ? " aggregate value " : " non-aggregate value");
            else
                message = t("slices.validExpression1") + (data.IsAggregate ? "an aggregate value " : "a non-aggregate value");

            dispatch(addNotification({text: message, type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
        }
        else    
        {
            message = t("slices.invalidExpression");
            dispatch(addNotification({text: message, type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
        }

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const { setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, datasetLoaded, currentFieldGroupSelected, createEntity, setFormValue, setFormClientErrors, setFormServerErrors, entityLoaded, removeEntityFromUI, entityInserted, entityUpdated, entityCloned, entityDeleted, insertCodeSnippet, insertFunction, disposePage } = expressionsPageSlice.actions;

